import React from 'react';
import styled from 'styled-components';
import { Border, Breakpoints, Color, Grid, Type } from '../../../app/StyleGuide';
import { Container, ContainerFluid } from '../../../components/Layout';
import { MediumOutlineButton } from '../../../components/Buttons';
import { sanitizeHTML } from '../../../utils/sanitizeHTML';

const HeaderContainer = styled(ContainerFluid)`
  background: ${Color.white};
  border-bottom: 1px solid ${Color.Gray._20};
`;

const HeaderInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Grid._5};
  padding: ${Grid._5} 0;
`;

const TitleRow = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  gap: ${Grid._3};
`;

const Title = styled.h1`
  margin: 0;
  font-size: ${Type.Scale._5};
  font-weight: ${Type.Weight.bold};
  color: ${Type.Color.dark};
  line-height: ${Type.Leading.normal};

  @media screen and (min-width: ${Breakpoints.screen_sm}) {
    font-size: ${Type.Scale._6};
  }
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: ${Type.Scale._2};
  font-weight: ${Type.Weight.normal};
  color: ${Type.Color.medium};
`;

const AboutSection = styled.section`
  display: block;
`;

const AboutTitle = styled.h3`
  margin: 0 0 ${Grid._2};
  font-size: ${Type.Scale._3};
  font-weight: ${Type.Weight.bold};
  color: ${Type.Color.dark};
`;

const AboutIntroduction = styled.p`
  margin: 0;
  font-size: ${Type.Scale._2};
  font-weight: ${Type.Weight.normal};
  color: ${Type.Color.medium};
  line-height: ${Type.Leading.taller};
`;

const Note = styled.div`
  margin-top: ${Grid._4};
  padding: ${Grid._4};
  background-color: ${Color.Gray._20};
  border-radius: ${Border.radius};
  border: 1px solid ${Color.Gray._30};

  p {
    margin: 0;
    font-size: ${Type.Scale._2};
    font-weight: ${Type.Weight.normal};
    color: ${Type.Color.medium};
    line-height: ${Type.Leading.taller};

    strong {
      font-weight: ${Type.Weight.bold};
      color: ${Type.Color.dark};
    }
  }
`;

const PrintActionsRow = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${Grid._3};
  align-items: flex-start;

  @media screen and (min-width: ${Breakpoints.screen_sm}) {
    flex-direction: row;
    justify-content: space-between;
    gap: ${Grid._4};
  }
`;

type AssessmentReportHeaderProps = {
  title: string;
  subtitle: string;
  introduction: string;
};

export const AssessmentReportHeader = ({ title, subtitle, introduction }: AssessmentReportHeaderProps) => {
  const handlePrint = () => {
    if (navigator.userAgent.toLowerCase().includes('safari')) {
      document.execCommand('print', false, undefined);
    } else {
      window.print();
    }
  };
  return (
    <HeaderContainer>
      <Container>
        <HeaderInner>
          <PrintActionsRow>
            <TitleRow>
              <Title>{title}</Title>
              <Subtitle>{subtitle}</Subtitle>
            </TitleRow>
            <MediumOutlineButton type="button" onClick={handlePrint}>
              <i className="fas fa-print" aria-hidden="true" />
              Print Report
            </MediumOutlineButton>
          </PrintActionsRow>

          <AboutSection>
            <AboutTitle>About This Assessment</AboutTitle>
            <AboutIntroduction dangerouslySetInnerHTML={{ __html: sanitizeHTML(introduction) }} />
            <Note>
              <p>
                <strong>Note:</strong> This assessment is a tool for spiritual growth and pastoral care. Use these
                insights to celebrate areas of strength, identify opportunities for deeper discipleship, and develop
                targeted ministry strategies that help your congregation grow in Christ-likeness.
              </p>
            </Note>
          </AboutSection>
        </HeaderInner>
      </Container>
    </HeaderContainer>
  );
};

export default AssessmentReportHeader;
